import { Observable, of, tap, delay, Subject, takeUntil, merge, switchMap } from 'rxjs';

const defaultLoadingStatusTimeLimit = 500;

/**
 * Passes through the given source, but if the source takes longer than
 * the configured time, a loading status will be sent first.
 * Optionally allows forcing a delay.
 *
 * @param source The data source
 * @param fallback The value that should be sent if the data source is delayed
 * @param gracePeriod The time to wait for the data source before sending a loading status
 * @param forcedDelay The time to wait before the data source is queried, e.g. to force a loading status
 */
export const fallbackIfDelayed = <T>(
    source: Observable<T>,
    fallback: T,
    options: { gracePeriod?: number; forcedDelay?: number } = { gracePeriod: defaultLoadingStatusTimeLimit, forcedDelay: 0 },
): Observable<T> => {
    const doneLoading$ = new Subject<undefined>();

    return merge(
        of(fallback)
            .pipe(
                delay(options.gracePeriod ?? 0),
                takeUntil(doneLoading$),
            ),
        of(undefined)
            .pipe(
                delay(options.forcedDelay ?? 0),
                switchMap(() => source),
                tap(() => { doneLoading$.next(undefined); }),
            ),
    );
};
